// frontend/src/features/production/serialization/api/serializationApi.ts
//
// ADR-104 frontend — ALL orval-generated hook imports for this feature are
// isolated in this ONE file. Every page/component in this feature imports
// ONLY from here, never directly from "@/api/generated/v1/v1".

import {
  // SerialUnit
  useV1ProductionSerialUnitsList,
  useV1ProductionSerialUnitsCreate,
  useV1ProductionSerialUnitsRetrieve,
  useV1ProductionSerialUnitsTraceRetrieve,
  useV1ProductionSerialUnitsLinkComponentCreate,
  // WorkCenterLotFeedSession
  useV1ProductionLotFeedSessionsList,
  useV1ProductionLotFeedSessionsCreate,
  useV1ProductionLotFeedSessionsCloseCreate,
  // SerialUnitLotLink (read-only)
  useV1ProductionSerialUnitLotLinksList,
  // InventoryLotAffectedUnitsView
  useV1InventoryLotsAffectedUnitsRetrieve,
} from "@/api/generated/v1/v1";

// ---------------------------------------------------------------------------
// Types (mirrors the DRF serializers — see ADR-104 backend delivery)
// ---------------------------------------------------------------------------

export type SerialUnitSourceMode = "at_lot_intake" | "during_production" | "at_completion";

export interface SerialUnit {
  id: string;
  material_revision: string;
  serial_number: string;
  source_mode: SerialUnitSourceMode;
  work_order: string | null;
  routing_operation: string | null;
  genealogy_snapshot: GenealogyNode | null;
  genealogy_frozen_at: string | null;
  created_at: string;
}

export interface GenealogyNode {
  serial_number: string;
  material_revision_id: string;
  source_mode: SerialUnitSourceMode;
  work_order_id: string | null;
  routing_operation_id: string | null;
  lot_numbers: string[];
  components: GenealogyNode[];
}

export interface DirectLotLink {
  lot_id: string;
  lot__lot_number: string;
  lot__received_qty: string;
}

export interface SerialUnitTrace {
  serial_unit_id: string;
  serial_number: string;
  frozen: boolean;
  genealogy: GenealogyNode | null;
  direct_lot_links: DirectLotLink[];
  work_order_id: string | null;
}

export interface WorkCenterLotFeedSession {
  id: string;
  work_center: string;
  lot: string;
  lot_number: string;
  started_at: string;
  ended_at: string | null;
}

export interface AffectedUnit {
  id: string;
  serial_number: string;
  material_revision_id: string;
  work_order_id: string | null;
}

// ---------------------------------------------------------------------------
// Hooks re-exported with a stable, feature-local name. Pages import these.
// ---------------------------------------------------------------------------

export function useSerialUnitList(params?: { serial_number?: string; work_order?: string }) {
  return useV1ProductionSerialUnitsList(params as any, { query: {} });
}

export function useSerialUnitRetrieve(id: string | undefined) {
  return useV1ProductionSerialUnitsRetrieve(id as string, { query: { enabled: !!id } });
}

export function useSerialUnitCreate() {
  return useV1ProductionSerialUnitsCreate();
}

export function useSerialUnitTrace(id: string | undefined) {
  return useV1ProductionSerialUnitsTraceRetrieve(id as string, { query: { enabled: !!id } });
}

export function useSerialUnitLinkComponent() {
  return useV1ProductionSerialUnitsLinkComponentCreate();
}

export function useLotFeedSessionList(params?: { work_center?: string; open?: boolean }) {
  return useV1ProductionLotFeedSessionsList(params as any, { query: {} });
}

export function useLotFeedSessionOpen() {
  return useV1ProductionLotFeedSessionsCreate();
}

export function useLotFeedSessionClose() {
  return useV1ProductionLotFeedSessionsCloseCreate();
}

export function useSerialUnitLotLinkList(params?: { serial_unit?: string; lot?: string }) {
  return useV1ProductionSerialUnitLotLinksList(params as any, { query: {} });
}

export function useAffectedUnits(lotId: string | undefined) {
  return useV1InventoryLotsAffectedUnitsRetrieve(lotId as string, { query: { enabled: !!lotId } });
}
